export interface PricingCta {
  label: string;
  href: string;
}

export interface PricingTier {
  id: 'free' | 'team' | 'scale';
  name: string;
  price: string;
  priceNote: string;
  bullets: string[];
  cta: PricingCta;
  highlight: boolean;
}

export const pricingTiers: PricingTier[] = [
  {
    id: 'free',
    name: 'Free',
    price: '$0',
    priceNote: 'forever',
    bullets: [
      '200 Tessa-minutes every month',
      'Phone calls unlock when you verify your phone and add a card ($0 — never charged automatically); web check-ins from day one, card-free',
      '1 builder',
      'Unlimited free members',
      'All templates, all channels',
    ],
    cta: { label: 'Create your first routine — free', href: '/routines/new' },
    highlight: false,
  },
  {
    id: 'team',
    name: 'Team',
    price: '$99',
    priceNote: 'per Builder seat / month · $990 billed annually (2 months free)',
    bullets: [
      'The operator seat: for founders, executives, and the leads who deploy routines & behaviors',
      'Run reports after every round — trends, sentiment, blockers, and who can unblock whom',
      '100 pooled minutes per seat, then top up at 15¢ a minute — packs never expire',
      'Billed per second — no-answer calls cost nothing',
      'Full studio on every recorded call — included',
      "Free Insights access for supervisors — they read, they don't burn the pool",
      // Honest scope: the connector is read-only and builder-gated (owners &
      // admins with a seat) — never imply supervisors/members get it, and
      // never name assistants we haven't verified. Verification record:
      // Claude verified END-TO-END live 2026-07-14 (real claude.ai connector;
      // first customer connect 2026-07-15). ChatGPT verified END-TO-END live
      // 2026-07-15: real ChatGPT client completed DCR + OAuth (mcp_clients
      // 'ChatGPT' 15:33 UTC, mcp_connect_completed) and executed three tools
      // (list_my_domains, list_posts, get_team_feed). Gemini's consumer app
      // has no custom-connector flow (CLI only), so "MCP" carries the rest.
      'Ask your data anything from Claude or ChatGPT — read-only MCP connector for builders',
      'Builders are owners & admins only; members are always free',
      'Per-routine minute budgets',
    ],
    // Static fallback only. PublicPricingTable overrides this at render time
    // with the auth-aware, billing-period-aware destination so a high-intent
    // click lands one step from checkout (logged-in → billing?intent=team;
    // logged-out → /login carrying the same intent as ?next=). This monthly
    // logged-out target is the safe default if the href is read directly.
    cta: { label: 'Start with Team', href: '/login?signup=true&next=%2Fdashboard%2Fsettings%2Fbilling%3Fintent%3Dteam' },
    highlight: true,
  },
  {
    id: 'scale',
    name: 'Company',
    price: 'Bundles',
    priceNote: 'platform + shared minute pool, shaped to your org',
    bullets: [
      'One bundle, not a seat grid: Builder seats + a pooled minute line + rollout support',
      'Org-wide routines — every employee, on their channel, in their language',
      'Compliance pack in writing: aggregate-first reporting, no individual scoring, DPA',
      'Start with a pilot on one team or division',
      'Local numbers per country · SSO · SLA — arranged together',
    ],
    cta: { label: 'Design your bundle', href: 'mailto:hello@apprendo.io?subject=Company%20bundle' },
    highlight: false,
  },
];

// The canonical "how billing works" sentence (hero copy on /pricing + the
// landing pricing section). The included-minutes clause is LOAD-BEARING:
// without it, "15¢ a Tessa-minute" reads as an unconditional per-minute charge
// and overstates the bill (each seat pools 100 included minutes first).
export const oneSentenceBill =
  "The $99 Builder seat is for the people who deploy strategy — founders, executives, and the leads who design Tessa's routines and behaviors. Each seat pools 100 minutes of Tessa's talk time, then it's 15¢ a minute; supervisors who read the results and everyone Tessa checks in on are free.";

export const guaranteeStrip: string[] = [
  'A minute is a minute — no credits, no tokens; see the rate and where every minute went',
  'Pay for minutes, not for people — inviting someone never costs a seat',
  'You control the pool — budget minutes across routines',
];
